import { useQuery } from "@tanstack/react-query";
import { useRestAPI } from "./axiosAPI";
import { useCentralAPI, WorkItemTypeCentral } from "./centralAPI";
import { calculateWorkingHours } from "src/pages/LayoutRoot/pages/Master/my_works/component.projectCard";

const QUERY_KEY = 'single-work-item'

// Interface for user identity (used in CreatedBy, ChangedBy, AssignedTo, etc.)
interface Identity {
  displayName: string;
  url: string;
  _links: {
    avatar: {
      href: string;
    };
  };
  id: string;
  uniqueName: string;
  imageUrl: string;
  descriptor: string;
}

// Interface for the fields object within each revision
interface WorkItemFields {
  'System.WorkItemType': string;
  'System.State': 'To Do' | 'Doing' | 'On Hold' | 'Done';
  'System.Reason': string;
  'System.AssignedTo'?: Identity;
  'System.CreatedDate': string;
  'System.CreatedBy': Identity;
  'System.ChangedDate': string;
  'System.ChangedBy': Identity;
  'System.CommentCount': number;
  'System.TeamProject': string;
  'System.AreaPath': string;
  'System.IterationPath': string;
  'System.Title': string;
  'System.BoardColumn'?: string;
  'System.BoardColumnDone'?: boolean;
  'Microsoft.VSTS.Common.StateChangeDate': string;
  'Microsoft.VSTS.Common.Priority': number;
  'Microsoft.VSTS.Common.StackRank'?: number;
  'WEF_83D4BB965F9F4862A673CB4FCC34719B_Kanban.Column'?: string;
  'WEF_83D4BB965F9F4862A673CB4FCC34719B_Kanban.Column.Done'?: boolean;
  "System.Parent"?: number
  [key: string]: any; // For additional fields that might appear
}

// Interface for each revision object
export interface WorkItemRevision {
  id: number;
  rev: number;
  fields: WorkItemFields;
  url: string;
}

// Interface for the full response
export interface WorkItemRevisionsResponse {
  count: number;
  value: WorkItemRevision[];
}

export function useWorkItemsAPI({
  retryCount = 1,
  trigger = true,
  id,
  organization
}: {
  retryCount?: number,
  trigger?: boolean,
  id: number,
  organization: string
}) {

  const api = useRestAPI()







  const {
    data: workItem,
    isLoading,
    isError,
    refetch: refetchWorkItem,
    isFetching,
    isFetched,
    error
  } = useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      console.log(organization)
      const response = await api.get<WorkItemRevisionsResponse>({
        url: `https://dev.azure.com/${organization}/_apis/wit/workItems/${id}/revisions?api-version=7.0`,
        mode: 'protected',
      })
      if (response.success === false) {
        throw response.msg
      }
      return response


    },
    refetchOnWindowFocus: false,
    retry: retryCount,
    enabled: trigger

  })

  return {
    workItem,
    isLoading,
    isError,
    refetchWorkItem,
    isFetching,
    isFetched,
    error
  }


}


export interface DayWiseEntry {
  date: string;
  workItems: (WorkItemTypeCentral&{hour:number})[];
}
interface OutputRevision {
  rev: number;
  state: string;
  changedDate: string;
}


export function useWorkItemsAPIAll() {

  const {
    centralData
  } = useCentralAPI({
    trigger: false
  })

  function filterWorkItemsByDateRange({
    workItems,
    fromDate,
    toDate,
    organization,
    userId
  }: {
    workItems: WorkItemTypeCentral[],
    fromDate: Date, toDate: Date, organization?: string,
    userId?: string
  }) {
    // Convert input dates to Date objects
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    const user = userId ? centralData?.Users.find(user => user.id.includes(userId)) : undefined

    // Validate dates
    if (isNaN(startDate as any) || isNaN(endDate as any)) {
      throw new Error("Invalid date format for fromDate or toDate");
    }
    if (startDate > endDate) {
      throw new Error("fromDate cannot be later than toDate");
    }

    // Filter work items based on date range or Doing condition
    return workItems.filter(item => {



      // Sort revisions by rev to ensure correct order
      const sortedRevisions = [...item.revisions].sort((a, b) => a.rev - b.rev);
      //filter by user if user available
      if (user) {
        if (!user.id.includes(sortedRevisions[sortedRevisions.length - 1].fields["System.AssignedTo"]?.id || '')) {
          return false
        }
      }
      //filter organization
      if (organization && organization !== item.organizationId) {
        return false
      }

      // Check original condition: at least one revision within date range
      const hasRevisionInDateRange = sortedRevisions.some(revision => {
        const fields = revision.fields;
        if (!fields) return false;
        const changedDate = new Date(fields["System.ChangedDate"]);
        return changedDate >= startDate && changedDate <= endDate;
      });



      // Check new Doing condition
      const hasDoingBeforeFromDate = sortedRevisions.some((revision, index) => {
        const fields = revision.fields;
        if (!fields) return false;
        const changedDate = new Date(fields["System.ChangedDate"]);
        const isDoing = fields["System.State"] === "Doing";
        const isBeforeFromDate = changedDate < startDate;

        // Check if this is the last revision or the next revision is after fromDate
        const isLastRevision = index === sortedRevisions.length - 1;
        let nextRevisionAfterFromDate = false;
        if (!isLastRevision) {
          const nextFields = sortedRevisions[index + 1].fields;
          if (nextFields) {
            const nextChangedDate = new Date(nextFields["System.ChangedDate"]);
            nextRevisionAfterFromDate = nextChangedDate > startDate;
          } else {
            // If next revision has no fields, treat it as no next revision
            nextRevisionAfterFromDate = true;
          }
        }

        return isDoing && isBeforeFromDate && (isLastRevision || nextRevisionAfterFromDate);
      });

      // Return work item if either condition is met
      return hasRevisionInDateRange || hasDoingBeforeFromDate;
    });
  }
  function filterWorkItemsDayWiseByDateRange({
    workItems,
    fromDate,
    toDate,
    organization,
    userId
  }: {
    workItems: WorkItemTypeCentral[],
    fromDate: Date, toDate: Date, organization?: string,
    userId?: string
  }) {
    // Convert input dates to Date objects
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    const user = userId ? centralData?.Users.find(user => user.id.includes(userId)) : undefined


    // Helper function to convert date to YYYY-MM-DD
    function formatDateToString(date: Date): string {
      return date.toISOString().split('T')[0];
    }

    // Helper function to add days to a date
    function addDays(date: Date, days: number): Date {
      const result = new Date(date);
      result.setDate(result.getDate() + days);
      return result;
    }

    // Main function to process work items
    function getDayWiseWorkItems(workItems: WorkItemTypeCentral[], startDate?: Date, endDate?: Date): DayWiseEntry[] {
      const dayWiseWorkItems: { [date: string]: WorkItemTypeCentral[] } = {};

      // Process each work item
      workItems.forEach(workItem => {
        const workItemId = workItem.id;
        const revisions = workItem.revisions;
        const title = revisions[0].fields["System.Title"];

        // Get all ChangedDate values to find date range if not provided
        const dates = revisions.map(rev => new Date(rev.fields["System.ChangedDate"]));
        const minDate = new Date(Math.min(...dates as any));
        const maxDate = new Date(Math.max(...dates as any));

        // Use provided startDate/endDate or default to min/max
        const rangeStart = startDate ? new Date(startDate) : minDate;
        rangeStart.setHours(rangeStart.getHours() + 5, 30, 0, 0);
        const rangeEnd = endDate ? new Date(endDate) : maxDate;
        rangeEnd.setHours(rangeEnd.getHours() + 5, 59, 59, 999);

        // Iterate through each date in the range
        let currentDate = new Date(rangeStart);
        while (currentDate <= rangeEnd) {
          const dateStr = formatDateToString(currentDate);
          if (!dayWiseWorkItems[dateStr]) {
            dayWiseWorkItems[dateStr] = [];
          }

          // Collect relevant revisions for this date
          const relevantRevisions: WorkItemRevision[] = [];

          revisions.forEach((rev, index) => {
            const revDate = new Date(rev.fields["System.ChangedDate"]);
            const state = rev.fields["System.State"];
            const revDateStr = formatDateToString(revDate);

            // Condition 1: Revision's ChangedDate matches the current date
            if (revDateStr === dateStr) {
              relevantRevisions.push({
                // rev: rev.rev,

                // state: state,
                // changedDate: rev.fields["System.ChangedDate"],
                ...rev
              });
              return;
            }

            // Condition 2: State is "Doing" and date is between this and next revision
            if (state === "Doing") {
              const nextRevDate = index < revisions.length - 1
                ? new Date(revisions[index + 1].fields["System.ChangedDate"])
                : null;
              if (nextRevDate) {
                if (revDate <= currentDate && currentDate <= nextRevDate) {
                  relevantRevisions.push({
                    // rev: rev.rev,
                    // state: state,
                    // changedDate: rev.fields["System.ChangedDate"],
                    ...rev
                  });
                }
              } else if (revDate <= currentDate) {
                // Last revision is "Doing", assume it continues
                relevantRevisions.push({
                  // rev: rev.rev,
                  // state: state,
                  // changedDate: rev.fields["System.ChangedDate"],
                  ...rev
                });
              }
            }
          });

          // If there are relevant revisions, add the work item
          if (relevantRevisions.length > 0) {
            // Avoid duplicate revisions by using a Map
            // const uniqueRevisions = Array.from(new Map(
            //   relevantRevisions.map(r => [r.rev, r])
            // ).values());

            dayWiseWorkItems[dateStr].push({
              id: workItemId,
              // title: title,
              revisions: revisions,
              organizationId: workItem.organizationId
            });
          }

          currentDate = addDays(currentDate, 1);
        }
      });

      // Remove empty date entries and convert to array
      const result = Object.entries(dayWiseWorkItems)
        .filter(([_, items]) => items.length > 0)
        .map(([date, items]) => {
          return ({
            date, workItems: items.map(
              item => {
                // console.log(startDate,endDate)
                // Use provided startDate/endDate or default to min/max
                const rangeStart =  new Date(date);
                rangeStart.setHours(rangeStart.getHours() + 0, 0, 0, 0);
                const rangeEnd =  new Date(date);
                rangeEnd.setHours(rangeEnd.getHours() + 23, 59, 59, 999);
                const hour = calculateWorkingHours(item.revisions, rangeStart, rangeEnd);
                return {
                  ...item,
                  hour
                }
              }
            )
          })
        });

      return result;
    }

    // Validate dates
    if (isNaN(startDate as any) || isNaN(endDate as any)) {
      throw new Error("Invalid date format for fromDate or toDate");
    }
    if (startDate > endDate) {
      throw new Error("fromDate cannot be later than toDate");
    }

    // Filter work items based on date range or Doing condition
    const filteredWorkItems = workItems.filter(item => {



      // Sort revisions by rev to ensure correct order
      const sortedRevisions = [...item.revisions].sort((a, b) => a.rev - b.rev);
      //filter by user if user available
      if (user) {
        if (!user.id.includes(sortedRevisions[sortedRevisions.length - 1].fields["System.AssignedTo"]?.id || '')) {
          return false
        }
      }
      //filter organization
      if (organization && organization !== item.organizationId) {
        return false
      }

      // Check original condition: at least one revision within date range
      const hasRevisionInDateRange = sortedRevisions.some(revision => {
        const fields = revision.fields;
        if (!fields) return false;
        const changedDate = new Date(fields["System.ChangedDate"]);
        return changedDate >= startDate && changedDate <= endDate;
      });



      // Check new Doing condition
      const hasDoingBeforeFromDate = sortedRevisions.some((revision, index) => {
        const fields = revision.fields;
        if (!fields) return false;
        const changedDate = new Date(fields["System.ChangedDate"]);
        const isDoing = fields["System.State"] === "Doing";
        const isBeforeFromDate = changedDate < startDate;

        // Check if this is the last revision or the next revision is after fromDate
        const isLastRevision = index === sortedRevisions.length - 1;
        let nextRevisionAfterFromDate = false;
        if (!isLastRevision) {
          const nextFields = sortedRevisions[index + 1].fields;
          if (nextFields) {
            const nextChangedDate = new Date(nextFields["System.ChangedDate"]);
            nextRevisionAfterFromDate = nextChangedDate > startDate;
          } else {
            // If next revision has no fields, treat it as no next revision
            nextRevisionAfterFromDate = true;
          }
        }

        return isDoing && isBeforeFromDate && (isLastRevision || nextRevisionAfterFromDate);
      });

      // Return work item if either condition is met
      return hasRevisionInDateRange || hasDoingBeforeFromDate;
    });

    const dayWiseWorkItems = getDayWiseWorkItems(filteredWorkItems, startDate, endDate);




    return dayWiseWorkItems
  }

  return {
    filterWorkItemsByDateRange,
    filterWorkItemsDayWiseByDateRange
  }

}